import { RefundModel } from 'api-hooks/refund/refund';
import stylesColors from 'common/styles/styles.colors';
import { generateId } from 'common/utils/string';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType, IColumn } from 'components/elements/table/type';
import Text from 'components/elements/text';
import { HeaderCheckbox } from 'legacy-modules/sales/reschedule/header-checkbox';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

interface Props {
  parentIndex: number;
  refund?: RefundModel;
}

function reducer(state, action, { update, clearErrors, name, setValue }) {
  switch (action.type) {
    case 'addRow':
      return {
        ...state,
        data: [...state.data, { customId: generateId() }],
      };
    case 'update_cell':
      return {
        ...state,
        data: state.data.map((row, index) => {
          if (index === action.rowIndex) {
            const tempValue = {
              ...state.data[action.rowIndex],
              [action.columnId]: action.value,
            };
            // update(index, tempValue);
            clearErrors(name);
            setValue(`${name}.${index}`, tempValue, { shouldValidate: true });
            return tempValue;
          }
          return row;
        }),
      };
    case 'update_all':
      return {
        ...state,
        data: action.newData,
      };
    case 'isSelected':
      return {
        ...state,
        data: state.data.map((row, index) => {
          const tempValue = {
            ...state.data[index],
            isSelected: action.value,
          };
          clearErrors(name);
          // update(index, tempValue);
          setValue(`${name}.${index}`, tempValue, { shouldValidate: true });

          return tempValue;
        }),
      };
    default:
      return state;
  }
}

const _columns: (props: {
  t: any;
  editable?: boolean;
  name: string;
  dispatch;
}) => IColumn<any>[] = ({ t, editable, dispatch, name }) => [
  {
    header: () => <HeaderCheckbox name={name} dispatch={dispatch} />,
    accessorKey: 'isSelected',
    minSize: 50,
    size: 50,
    checkboxNoLabel: true,
    dataType: ColumnDataType.checkbox,
  },
  {
    header: t('common:guest'),
    accessorKey: 'guestName',
    minSize: 150,
    dataType: ColumnDataType.text,
    disabled: true,
  },
  {
    header: t('common:description'),
    accessorKey: 'description',
    minSize: 100,
    dataType: ColumnDataType.text,
    disabled: true,
  },
  {
    header: t('common:price'),
    accessorKey: 'price',
    minSize: 100,
    dataType: ColumnDataType.number,
    minNumber: 0,
    disabled: true,
  },
  {
    header: t('common:refund_price'),
    accessorKey: 'refundPrice',
    minSize: 100,
    dataType: ColumnDataType.number,
    minNumber: 0,
  },
];

export default function AddonsTourTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const {
    control,
    formState: { isSubmitting },
    clearErrors,
    setValue,
  } = useFormContext();

  //TODO CHANGE NAME
  const name: any = `tourSales.0.tourSaleAddOns`;

  const { fields, update } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const columns = (dispatch?: any) => _columns({ t, editable, dispatch, name });

  const [isInitiate, setIsInitiate] = React.useState<{
    value: boolean;
    data: any;
  }>({ value: false, data: undefined });

  const [state, dispatch] = React.useReducer(
    (state, action) =>
      reducer(state, action, { update, clearErrors, name, setValue }),
    {
      columns: columns(),
      data: [],
    },
  );

  React.useEffect(() => {
    if (!isInitiate.value) {
      dispatch({
        type: 'update_all',
        newData: fields.length ? fields : [],
      });
      setIsInitiate({
        value: true,
        data: fields.length ? fields : [],
      });
    }
  }, [fields, isInitiate]);

  React.useEffect(() => {
    if (!editable && isInitiate.value && !isSubmitting) {
      dispatch({ type: 'update_all', newData: isInitiate.data });
    }
  }, [editable, isInitiate.value, isSubmitting, t, isInitiate.data]);

  if (fields.length === 0) {
    return null;
  }

  return (
    <>
      <Separator gap={24} />
      <Separator gap={16} direction="vertical">
        <Text
          variant="LabelMedium"
          $style={{ color: stylesColors.primaryDefault }}
        >
          {t('document:addons')}
        </Text>
      </Separator>
      <div style={{ width: '100%' }}>
        <TableInput
          columns={columns(dispatch)}
          data={state.data}
          dispatch={dispatch}
          name={name}
          disabled={!editable || !!props.refund}
          uniqueRowKey="customId"
          noAddRemoveRow
          onCustomDisabledCell={({ accessorKey, rowOriginal }) =>
            !!(accessorKey !== 'isSelected' && !rowOriginal.isSelected)
          }
        />
      </div>
    </>
  );
}
